import * as React from 'react';
import { ClipboardList, FileText } from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import type { MeetingAnalytics } from '../types';
import { dateOrDash, textOrDash } from '../format';
import { Section } from './Section';

/**
 * Outcome, next step and the free-text notes. Notes are rendered as PLAIN TEXT
 * (whitespace-preserving, never dangerouslySetInnerHTML) so user-entered content
 * can't inject markup. Absent notes show a small empty note, not a blank gap.
 */
export function OutcomeNotesCard({ data }: { data: MeetingAnalytics }) {
  const hasNotes = !!data.notesText && data.notesText.trim().length > 0;

  return (
    <Section
      title="Outcome & notes"
      icon={<ClipboardList className="h-4 w-4" aria-hidden="true" />}
      aside={
        <Badge variant={data.notesSubmitted ? 'success' : 'soft'} size="sm">
          {data.notesSubmitted ? 'Submitted' : 'Draft'}
        </Badge>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <Field label="Outcome" value={textOrDash(data.outcome)} />
          <Field label="Next step" value={dateOrDash(data.nextStepDate)} />
        </div>

        <div>
          <p className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
            Notes
          </p>
          {hasNotes ? (
            <p className="whitespace-pre-wrap break-words rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface-hover)] p-4 text-sm leading-relaxed text-[var(--text-secondary)]">
              {data.notesText}
            </p>
          ) : (
            <div className="flex items-center gap-2.5 rounded-[var(--radius-md)] border border-dashed border-[var(--border)] px-4 py-3.5 text-sm text-[var(--text-muted)]">
              <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
              No notes recorded for this call.
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-[var(--text-primary)]">{value}</p>
    </div>
  );
}
